// Client for the local fingerprint bridge.
//
// The ZKTeco SLK20R is a USB device, so the browser cannot read it directly.
// A small service runs on the delivery-desk PC, owns the device through the
// ZKFinger SDK, and exposes the three calls below on localhost. This module is
// the only place that knows how to talk to it — see FINGERPRINT_BRIDGE.md for
// the contract the service must implement.
//
// Until that service exists, simulation mode (a switch on the Developer page)
// makes every call return a believable fake so the whole counter flow can be
// operated and demonstrated. Simulated results are labelled as such all the
// way into the audit log and are never mistaken for a real match.

const jsonHeaders = { 'Content-Type': 'application/json' };

class BridgeError extends Error {
  constructor(message, { unreachable = false } = {}) {
    super(message);
    this.name = 'BridgeError';
    this.unreachable = unreachable;
  }
}

async function callBridge(bridgeUrl, path, body, timeoutSeconds = 20) {
  const base = String(bridgeUrl || '').replace(/\/+$/, '');
  if (!base) throw new BridgeError('No fingerprint bridge address is configured.', { unreachable: true });

  const controller = new AbortController();
  // The bridge waits for a finger on the platen, so the timeout has to cover a
  // human reaction, not just network latency.
  const timer = setTimeout(() => controller.abort(), Math.max(timeoutSeconds, 5) * 1000 + 2000);

  try {
    const response = await fetch(`${base}${path}`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    const text = await response.text();
    let data = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      throw new BridgeError(`Scanner service returned something unreadable: ${text.slice(0, 120)}`);
    }
    if (!response.ok) {
      throw new BridgeError(data.message || `Scanner service error (HTTP ${response.status}).`);
    }
    return data;
  } catch (err) {
    if (err instanceof BridgeError) throw err;
    if (err.name === 'AbortError') {
      throw new BridgeError('The scanner did not respond in time. Check the device and try again.');
    }
    // A failed fetch to localhost almost always means the service is not running.
    throw new BridgeError(
      'Cannot reach the fingerprint scanner service on this computer. Make sure it is running, then try again.',
      { unreachable: true }
    );
  } finally {
    // Without this the abort fires long after the call returned, and a later
    // capture on the same page would be cancelled by a stale timer.
    clearTimeout(timer);
  }
}

// --- Simulation -------------------------------------------------------------
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// A stable pseudo-template so a simulated enroll and a simulated verify of the
// same user look consistent.
const fakeTemplate = (seed, index) =>
  btoa(`SIMULATED-TEMPLATE:${seed}:${index}`).replace(/=+$/, '');

async function simulateCapture(index) {
  await wait(700); // let the operator see the "place finger" prompt
  return {
    template: fakeTemplate('capture', index),
    quality: 78 + ((index * 7) % 15),
    device: 'SIMULATED-SLK20R',
    simulated: true,
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export async function checkBridge(config) {
  if (config?.simulationMode) {
    return { ok: true, simulated: true, device: 'SIMULATED-SLK20R', version: 'simulation' };
  }
  try {
    const data = await callBridge(config?.bridgeUrl, '/status', {}, 5);
    return { ok: Boolean(data.ready ?? true), ...data };
  } catch (err) {
    return { ok: false, message: err.message, unreachable: err.unreachable };
  }
}

// Capture one finger for enrollment. Returns a template to store.
export async function captureFinger(config, { index, label }) {
  if (config?.simulationMode) return simulateCapture(index);

  const data = await callBridge(
    config?.bridgeUrl,
    '/capture',
    { index, label, timeout: config?.captureTimeoutSeconds || 20 },
    config?.captureTimeoutSeconds
  );
  if (!data.template) throw new BridgeError(data.message || 'The scanner returned an empty capture.');
  return {
    template: data.template,
    quality: Number(data.quality || 0),
    device: data.device || 'SLK20R',
    simulated: false,
  };
}

// Capture a live finger and match it against the user's stored templates.
// The matching happens on the PC — the SDK owns the algorithm and the
// templates never have to be compared in the browser.
export async function verifyFinger(config, { challenge, templates }) {
  if (config?.simulationMode) {
    await wait(900);
    return {
      matched: true,
      score: 92,
      fingerIndex: templates?.[0]?.index ?? 1,
      simulated: true,
      // No signature: simulated verdicts are never trusted by the server.
      signature: '',
    };
  }

  const data = await callBridge(
    config?.bridgeUrl,
    '/verify',
    {
      challenge,
      threshold: config?.matchThreshold || 55,
      timeout: config?.captureTimeoutSeconds || 20,
      templates: (templates || []).map((t) => ({
        index: t.index,
        label: t.label,
        template: t.template,
      })),
    },
    config?.captureTimeoutSeconds
  );

  return {
    matched: Boolean(data.matched),
    score: Number(data.score || 0),
    fingerIndex: Number(data.fingerIndex || 0),
    device: data.device || 'SLK20R',
    signature: data.signature || '',
    simulated: false,
    message: data.message || '',
  };
}

export { BridgeError };
